import { PaymentMethodEnum, ProviderEnum } from '@/types/common';
import { stripeService } from '@/services/stripe';
import {
  PaymentStatus,
  StripePaymentIntentStatus,
  type PaymentHandler,
  type PaymentHandlerWithTab,
  type PaymentStatusResponse,
} from '@/types/payment';
import { REDIRECT_PAYMENT_POLLING_INTERVAL } from '@/constants/payment';
import { sleep } from '@/utils/payment';
import { InternalMessageEventsName } from '@/constants/iframeMessages';
import { getNuveiPaymentStatus } from '@/utils/nuvei';
import { mapAdyenResultCodeToPaymentStatus } from '@/utils/adyen';
import { logError } from '@/utils/logger';
import { canCancelRedirectPaymentManually } from '@/utils/redirectPayment';

export class StripePaymentHandler implements PaymentHandler {
  constructor(private paymentMethod?: PaymentMethodEnum) {}

  async poll(
    clientSecret: string,
    __: (redirectResult: string) => Promise<PaymentStatusResponse>,
    ___: string,
    isTabClosed: boolean
  ): Promise<{ status: PaymentStatus; params: Record<string, unknown> }> {
    const stripe = stripeService.getStripe();
    const result = await stripe.retrievePaymentIntent(clientSecret);
    const intentStatus = result?.paymentIntent?.status;
    const isAfterpayIntent = this.isAfterpayPayment();

    if (intentStatus === StripePaymentIntentStatus.SUCCEEDED) {
      return { status: PaymentStatus.SUCCEEDED, params: result };
    }

    if (intentStatus === StripePaymentIntentStatus.REQUIRES_PAYMENT_METHOD) {
      // Mirror BE finalize-payment behavior: prefer the issuer decline_code,
      // fall back to the Stripe error type. Anything unknown -> GENERIC_DECLINE.
      const lastPaymentError = result?.paymentIntent?.last_payment_error;
      const appChargeErrorType = stripeService.mapStripeErrorCode(
        lastPaymentError?.decline_code ?? lastPaymentError?.type
      );
      return { status: PaymentStatus.FAILED, params: { ...result, appChargeErrorType } };
    }

    // Afterpay/Clearpay is an asynchronous (delayed-notification) APM. Once the
    // shopper authorizes, Stripe can move the intent to `processing`; per Stripe
    // docs this is NOT final paid/succeeded, only "submitted and awaiting async
    // success/failure". The webhook/backend order state is the source of truth
    // for fulfillment. Do not keep the checkout trapped in redirect polling
    // waiting for the PI to become `succeeded`; hand off to order polling, and
    // show final success only when the order poll returns SUCCESS.
    if (intentStatus === StripePaymentIntentStatus.PROCESSING && isAfterpayIntent) {
      return { status: PaymentStatus.PROCESSING, params: result };
    }

    // `isTabClosed` is not a usable abandon signal for Afterpay/Clearpay: their
    // hosted page sends `Cross-Origin-Opener-Policy: same-origin`, which swaps
    // the browsing context group on navigation and disowns our `WindowProxy`,
    // so `tab.closed` starts reporting `true` while the shopper is still paying.
    // Acting on it cancelled live sessions, so Afterpay only ever leaves this
    // loop on a real intent status (succeeded / declined / processing), on the
    // manager's ceiling, or when the shopper presses cancel. Other Stripe
    // redirect APMs keep a trustworthy handle and still cancel on tab close.
    if (isTabClosed && !isAfterpayIntent) {
      return { status: PaymentStatus.CANCELLED, params: result };
    }

    return { status: PaymentStatus.PENDING, params: result };
  }

  private isAfterpayPayment(): boolean {
    return this.paymentMethod === PaymentMethodEnum.AFTERPAY || this.paymentMethod === PaymentMethodEnum.CLEARPAY;
  }
}

class NuveiPaymentHandler implements PaymentHandler {
  async poll(
    clientSecret: string,
    __: (redirectResult: string) => Promise<PaymentStatusResponse>,
    paymentMethodId: string,
    isTabClosed: boolean
  ): Promise<{ status: PaymentStatus; params: Record<string, unknown> }> {
    try {
      if (!isTabClosed) {
        return { status: PaymentStatus.PENDING, params: {} };
      }
      const paymentStatusReponse = await getNuveiPaymentStatus(clientSecret, paymentMethodId);
      if (!paymentStatusReponse) {
        return { status: PaymentStatus.FAILED, params: {} };
      }
      if (
        paymentStatusReponse.status === 'ERROR' ||
        paymentStatusReponse.transactionStatus === 'ERROR' ||
        paymentStatusReponse.transactionStatus === 'DECLINED'
      ) {
        // Forward the gateway's own decline text so the SDK failure event can
        // attribute it; both fields are optional on the Nuvei payload.
        const { gwErrorReason, reason } = paymentStatusReponse;
        return { status: PaymentStatus.FAILED, params: { gwErrorReason, reason } };
      } else if (paymentStatusReponse.transactionStatus === 'APPROVED') {
        return { status: PaymentStatus.SUCCEEDED, params: {} };
      } else if (paymentStatusReponse.transactionStatus === 'PENDING') {
        return { status: PaymentStatus.PENDING, params: {} };
      }
      return { status: PaymentStatus.PENDING, params: {} };
    } catch (error: unknown) {
      return { status: PaymentStatus.FAILED, params: error as Record<string, unknown> };
    }
  }
}

class AdyenPaymentHandler implements PaymentHandler {
  private tab: Window | null = null;
  private lastTabUrl: string = '';
  private boundMessageHandler: ((event: MessageEvent) => void) | null = null;

  setTab(tab: Window | null): void {
    this.tab = tab;

    if (this.tab) {
      this.boundMessageHandler = this.handleTabMessage.bind(this);
      window.addEventListener('message', this.boundMessageHandler);
    }
  }

  private handleTabMessage(event: MessageEvent): void {
    if (event.source === this.tab) {
      console.log('handleTabMessage', event);
      this.lastTabUrl = event.data.url;
    }
  }

  private extractRedirectResult(): string | null {
    let redirectResult = new URLSearchParams(this.tab?.location.search).get('redirectResult');

    if (!redirectResult && this.lastTabUrl) {
      try {
        const urlParams = new URLSearchParams(new URL(this.lastTabUrl).search);
        redirectResult = urlParams.get('redirectResult');
      } catch {
        return null;
      }
    }

    return redirectResult;
  }

  async poll(
    _: string,
    pollPaymentStatus: (redirectResult: string) => Promise<PaymentStatusResponse>,
    ___: string,
    isTabClosed: boolean
  ): Promise<{ status: PaymentStatus; params: Record<string, unknown> }> {
    if (!isTabClosed) {
      return { status: PaymentStatus.PENDING, params: {} };
    }

    let redirectResult: string | null = null;
    try {
      redirectResult = this.extractRedirectResult();
    } catch {
      return { status: PaymentStatus.CANCELLED, params: {} };
    }

    if (redirectResult) {
      const { resultCode } = await pollPaymentStatus(redirectResult);
      return { status: mapAdyenResultCodeToPaymentStatus(resultCode), params: { resultCode } };
    }
    return { status: PaymentStatus.CANCELLED, params: {} };
  }

  cleanup(): void {
    if (this.boundMessageHandler) {
      window.removeEventListener('message', this.boundMessageHandler);
    }
    this.tab = null;
    this.lastTabUrl = '';
    this.boundMessageHandler = null;
  }
}

export class RedirectPaymentManager {
  private static readonly TAB_CLOSED_CHECK_INTERVAL = 100;
  private tab: Window | null = null;
  private polling = false;
  private handler: PaymentHandlerWithTab | null = null;

  constructor(
    private provider: ProviderEnum,
    private paymentMethod?: PaymentMethodEnum
  ) {
    this.initializeHandler();
  }

  private initializeHandler(): void {
    switch (this.provider) {
      case ProviderEnum.STRIPE:
        this.handler = new StripePaymentHandler(this.paymentMethod);
        break;
      case ProviderEnum.ADYEN:
        this.handler = new AdyenPaymentHandler();
        break;
      case ProviderEnum.NUVEI:
        this.handler = new NuveiPaymentHandler();
        break;
      default:
        logError('Unsupported provider in redirect payment manager', undefined, { provider: this.provider });
    }
  }

  setTab(tab: Window | null): void {
    this.tab = tab;

    if (this.handler?.setTab) {
      this.handler.setTab(this.tab);
    }
  }

  sendRedirectMessage(redirectUrl: string): void {
    if (!this.tab) {
      return;
    }
    // Navigate the opened tab directly to the PSP URL instead of relying on a
    // postMessage that ApmDynamicPage forwards into `window.location.href`.
    // The tab was opened synchronously inside the click handler to
    // `/apm-dynamic-page` (same origin as this window), so writing
    // `tab.location.href` is a legal opener->tab navigation regardless of the
    // final destination's origin, AND — critically — it does not require the
    // child page's React tree to have mounted and attached its `message`
    // listener. The previous flow was racy: on faster finalize + Stripe
    // `retrievePaymentIntent` round-trips the parent could dispatch
    // APM_REDIRECT before the child's useEffect ran, and the event was
    // silently discarded, leaving the tab stuck on the loader.
    try {
      this.tab.location.href = redirectUrl;
    } catch (error) {
      // Same-origin write shouldn't throw here, but if the browser has
      // already navigated the tab cross-origin (or something else denies the
      // write), fall back to the legacy postMessage path so ApmDynamicPage
      // can still forward the URL if it's mounted.
      logError('Failed to navigate redirect tab directly; falling back to postMessage', error, {
        redirectUrl,
      });
      this.tab.postMessage({ event: InternalMessageEventsName.APM_REDIRECT, params: { url: redirectUrl } }, '*');
    }
  }

  openTab(url: string): void {
    this.tab = window.open(url, '_blank');

    if (this.handler?.setTab) {
      this.handler.setTab(this.tab);
    }

    window.parent.focus();
  }

  closeTab(): void {
    if (this.tab && !this.tab.closed) {
      this.tab.postMessage({ event: InternalMessageEventsName.APM_REDIRECT_CLOSE, params: {} }, '*');
      if (this.requiresExplicitTabClose()) {
        this.tab.close();
      }
    }
    this.tab = null;
  }

  isTabClosed(): boolean {
    return this.tab?.closed ?? true;
  }

  async startPolling(
    clientSecret: string,
    onSuccess: (params: Record<string, unknown>) => void,
    onProcessing: (params: Record<string, unknown>) => void,
    onFailure: (error: string, params: Record<string, unknown>) => void,
    onCancel: (params: Record<string, unknown>) => void,
    pollPaymentStatus: (redirectResult: string) => Promise<PaymentStatusResponse>,
    paymentMethodId: string
  ): Promise<void> {
    if (!this.handler) {
      onFailure('Payment handler not initialized', {});
      return;
    }

    this.polling = true;

    while (this.polling) {
      await this.waitForNextPoll();

      if (!this.polling) {
        break;
      }

      const data = await this.handler.poll(clientSecret, pollPaymentStatus, paymentMethodId, this.isTabClosed());

      if (data.status === PaymentStatus.SUCCEEDED) {
        this.handleSuccess(onSuccess, data.params);
        break;
      }

      if (data.status === PaymentStatus.PROCESSING) {
        this.handleProcessing(onProcessing, data.params);
        break;
      }

      if (data.status === PaymentStatus.FAILED) {
        this.handleFailure(onFailure, data.params);
        break;
      }

      if (data.status === PaymentStatus.CANCELLED) {
        this.handleCancellation(onCancel, data.params);
        break;
      }
    }
  }

  private handleSuccess(onSuccess: (params: Record<string, unknown>) => void, params: Record<string, unknown>): void {
    this.polling = false;
    this.closeTab();
    window.parent.focus();
    onSuccess(params as Record<string, string>);
  }

  private handleProcessing(
    onProcessing: (params: Record<string, unknown>) => void,
    params: Record<string, unknown>
  ): void {
    this.polling = false;
    this.closeTab();
    window.parent.focus();
    onProcessing(params as Record<string, string>);
  }

  private handleFailure(
    onFailure: (error: string, params: Record<string, unknown>) => void,
    params: Record<string, unknown>
  ): void {
    this.polling = false;
    this.closeTab();
    window.parent.focus();
    onFailure('Payment was declined', params);
  }

  private handleCancellation(
    onCancel: (params: Record<string, unknown>) => void,
    params: Record<string, unknown>
  ): void {
    this.polling = false;
    this.closeTab();
    window.parent.focus();
    onCancel(params);
  }

  private requiresExplicitTabClose(): boolean {
    return this.provider === ProviderEnum.STRIPE;
  }

  private shouldWakeQuicklyOnTabClose(): boolean {
    if (this.provider !== ProviderEnum.STRIPE) {
      return true;
    }

    if (!this.paymentMethod || this.paymentMethod === PaymentMethodEnum.OTHER) {
      return false;
    }

    // Afterpay ignores `tab.closed`, so waking early for it buys nothing.
    return !this.isAfterpayFlow();
  }

  // The flows that lost their tab-close exit are exactly the ones the overlay
  // offers a manual cancel for, so both read the same predicate.
  private isAfterpayFlow(): boolean {
    return canCancelRedirectPaymentManually(this.provider, this.paymentMethod);
  }

  private async waitForNextPoll(): Promise<void> {
    const interval = REDIRECT_PAYMENT_POLLING_INTERVAL;

    if (!this.shouldWakeQuicklyOnTabClose()) {
      await sleep(interval);
      return;
    }

    let elapsed = 0;
    while (elapsed < interval && this.polling) {
      if (this.isTabClosed()) {
        return;
      }

      const delay = Math.min(RedirectPaymentManager.TAB_CLOSED_CHECK_INTERVAL, interval - elapsed);
      await sleep(delay);
      elapsed += delay;
    }
  }

  cleanup(): void {
    this.polling = false;
    this.closeTab();

    if (this.handler?.cleanup) {
      this.handler.cleanup();
    }
  }
}
